export const WHISPER_LIMIT_BYTES = 24 * 1024 * 1024;
export const CHUNK_SIZE_BYTES = 20 * 1024 * 1024;

export const AUDIO_MIME_MAP: Record<string, string> = {
  mp3: 'audio/mpeg',
  mp4: 'audio/mp4',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
};

// Container-based formats whose internal structure breaks when byte-split.
// These must be decoded to PCM first, then re-encoded as WAV for chunking.
export const CONTAINER_FORMATS = new Set(['m4a', 'mp4', 'ogg', 'webm']);

export function float32ToWav(samples: Float32Array, sampleRate: number): Blob {
  const numSamples = samples.length;
  const buf = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buf);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, numSamples * 2, true);
  let off = 44;
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([buf], { type: 'audio/wav' });
}

export async function decodeToWavChunks(file: File): Promise<Blob[]> {
  const SAMPLE_RATE = 16000;
  const MAX_SAMPLES = Math.floor((24 * 1024 * 1024 - 44) / 2);
  const arrayBuffer = await file.arrayBuffer();
  const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  await ctx.close();
  const channelData = audioBuffer.getChannelData(0);
  const chunks: Blob[] = [];
  for (let start = 0; start < channelData.length; start += MAX_SAMPLES) {
    chunks.push(float32ToWav(channelData.slice(start, start + MAX_SAMPLES), SAMPLE_RATE));
  }
  return chunks;
}

export function sliceAudioFile(file: File): Blob[] {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'mp3';
  const mimeType = file.type || AUDIO_MIME_MAP[ext] || 'audio/mpeg';
  const chunks: Blob[] = [];
  let offset = 0;
  while (offset < file.size) {
    const slice = file.slice(offset, offset + CHUNK_SIZE_BYTES);
    chunks.push(new Blob([slice], { type: mimeType }));
    offset += CHUNK_SIZE_BYTES;
  }
  return chunks;
}

export function estimateAudioDuration(file: File): string {
  const mb = file.size / (1024 * 1024);
  if (mb < 5) return '~1–3 min';
  if (mb < 20) return '~5–15 min';
  if (mb < 50) return '~15–40 min';
  if (mb < 100) return '~40–80 min';
  return '~80+ min';
}
